import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bell, BellOff, Loader2, ShieldCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

/**
 * PushRegistrationCard
 *
 * One-click enrollment for browser (web push) notifications. Registers the
 * service worker, asks the user for permission, subscribes to the browser's
 * push manager against our VAPID public key, then POSTs the subscription to
 * the backend. The backend fires push on: new DM, friend request, promo
 * broadcast, etc.
 *
 * Also handles unsubscribe (deletes from backend + browser subscription).
 */
function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

function browserSupportsWebPush() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export default function PushRegistrationCard() {
  const [supported, setSupported] = useState(true);
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "denied"
  );
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState({ web: 0, expo: 0 });
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);

  const refreshStatus = useCallback(async () => {
    try {
      const [{ data: cfg }, { data: st }] = await Promise.all([
        api.get("/push/config"),
        api.get("/push/status"),
      ]);
      setConfig(cfg);
      setStatus(st);
    } catch (err) {
      // non-fatal — surface a subtle error but don't disable the whole card
      console.warn("push config/status failed", err);
    }
  }, []);

  useEffect(() => {
    setSupported(browserSupportsWebPush());
    (async () => {
      await refreshStatus();
      // Check browser-side subscription too
      if (browserSupportsWebPush()) {
        try {
          const reg = await navigator.serviceWorker.getRegistration("/sw.js");
          const sub = reg ? await reg.pushManager.getSubscription() : null;
          setSubscribed(!!sub);
        } catch (e) {
          // ignore
        }
      }
      setLoading(false);
    })();
  }, [refreshStatus]);

  const subscribe = async () => {
    if (!supported) {
      toast.error("Your browser doesn't support push notifications");
      return;
    }
    if (!config?.web_push_configured || !config?.vapid_public_key) {
      toast.error("Push notifications aren't configured on the server yet");
      return;
    }
    setWorking(true);
    try {
      const reg =
        (await navigator.serviceWorker.getRegistration("/sw.js")) ||
        (await navigator.serviceWorker.register("/sw.js"));
      await navigator.serviceWorker.ready;
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error("Notification permission denied");
        return;
      }
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(config.vapid_public_key),
        });
      }
      const key = sub.getKey ? sub.getKey("p256dh") : null;
      const auth = sub.getKey ? sub.getKey("auth") : null;
      const b64 = (buf) =>
        btoa(String.fromCharCode(...new Uint8Array(buf)))
          .replace(/\+/g, "-")
          .replace(/\//g, "_")
          .replace(/=+$/, "");
      await api.post("/push/web/subscribe", {
        endpoint: sub.endpoint,
        keys: { p256dh: b64(key), auth: b64(auth) },
        ua: navigator.userAgent,
      });
      toast.success("Push notifications enabled");
      setSubscribed(true);
      refreshStatus();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message || "Push registration failed");
    } finally {
      setWorking(false);
    }
  };

  const unsubscribe = async () => {
    if (!supported) return;
    setWorking(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration("/sw.js");
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        await api
          .delete("/push/web/subscribe", { params: { endpoint: sub.endpoint } })
          .catch(() => {});
        await sub.unsubscribe();
      }
      toast.success("Notifications disabled on this device");
      setSubscribed(false);
      refreshStatus();
    } catch (err) {
      toast.error(err.message || "Couldn't unsubscribe");
    } finally {
      setWorking(false);
    }
  };

  return (
    <Card className="rounded-md border border-border p-6" data-testid="push-registration-card">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="h-10 w-10 rounded-full bg-[#B31E1E]/5 text-[#B31E1E] flex items-center justify-center shrink-0">
          {subscribed ? <Bell size={18} /> : <BellOff size={18} />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-xl">Push notifications</h3>
            {subscribed && (
              <Badge
                variant="outline"
                className="rounded-sm text-[10px] font-mono border-emerald-400 text-emerald-700"
                data-testid="push-registered-badge"
              >
                ENABLED
              </Badge>
            )}
            {status.expo > 0 && (
              <Badge
                variant="outline"
                className="rounded-sm text-[10px] font-mono border-sky-400 text-sky-700"
              >
                {status.expo} MOBILE DEVICE{status.expo === 1 ? "" : "S"}
              </Badge>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-0.5 max-w-2xl">
            Get instant alerts for direct messages, friend requests, and promo
            broadcasts — even when servrevu is closed. Works on Chrome, Firefox,
            Edge and Safari 16+. Nothing is sent to third parties.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="mt-5 flex items-center gap-2 text-sm text-gray-500">
          <Loader2 size={14} className="animate-spin" /> Checking status…
        </div>
      ) : !supported ? (
        <div className="mt-5 flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
          <ShieldAlert size={16} className="shrink-0 mt-0.5" />
          Your browser doesn&apos;t support the Push API. Try Chrome, Edge, Firefox,
          or Safari 16.4+.
        </div>
      ) : permission === "denied" ? (
        <div className="mt-5 flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
          <ShieldAlert size={16} className="shrink-0 mt-0.5" />
          Notifications are blocked at the browser level. Reset the permission
          in your site settings (padlock icon → Notifications) and reload.
        </div>
      ) : (
        <div className="mt-5 flex items-center gap-2 flex-wrap">
          {subscribed ? (
            <Button
              type="button"
              variant="outline"
              onClick={unsubscribe}
              disabled={working}
              className="rounded-[35px]"
              data-testid="push-unsubscribe-button"
            >
              {working ? (
                <><Loader2 size={14} className="mr-1.5 animate-spin" /> Working…</>
              ) : (
                <><BellOff size={14} className="mr-1.5" /> Turn off on this device</>
              )}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={subscribe}
              disabled={working}
              className="rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]"
              data-testid="push-subscribe-button"
            >
              {working ? (
                <><Loader2 size={14} className="mr-1.5 animate-spin" /> Enabling…</>
              ) : (
                <><ShieldCheck size={14} className="mr-1.5" /> Enable push notifications</>
              )}
            </Button>
          )}
          <span className="text-[11px] font-mono text-gray-400">
            {status.web} browser{status.web === 1 ? "" : "s"} enrolled on this account
          </span>
        </div>
      )}
    </Card>
  );
}
